
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import LaunchButton from '@/components/LaunchButton';
import { Checkbox } from '@/components/ui/checkbox';
import { Link } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';

interface SignupFormProps {
  email: string;
  isEmailReadOnly: boolean;
  isLoading: boolean;
  isRedirecting: boolean;
  showSubscriptionText: boolean;
  onSubmit: (formData: { name: string; email: string; password: string; confirmPassword: string; agreed: boolean }) => Promise<void>;
}

const SignupForm: React.FC<SignupFormProps> = ({ 
  email,
  isEmailReadOnly,
  isLoading,
  isRedirecting,
  showSubscriptionText,
  onSubmit
}) => {
  const [name, setName] = useState('');
  const [emailValue, setEmailValue] = useState(email);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreed, setAgreed] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!agreed) {
      toast({
        title: "Please agree to the terms",
        description: "You must agree to our terms and privacy policy to create an account.",
        variant: "destructive",
      });
      return;
    }
    
    if (password !== confirmPassword) {
      toast({
        title: "Passwords do not match",
        description: "Please make sure your passwords match.",
        variant: "destructive",
      });
      return;
    }
    
    if (password.length < 8) {
      toast({
        title: "Password too short",
        description: "Password must be at least 8 characters long.",
        variant: "destructive",
      });
      return;
    }
    
    await onSubmit({
      name,
      email: emailValue,
      password,
      confirmPassword,
      agreed
    });
  };

  // Update email state when prop changes
  React.useEffect(() => {
    if (email !== emailValue) {
      setEmailValue(email);
    }
  }, [email]);

  return (
    <Card className="w-full max-w-md shadow-xl">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold text-center">Create an account</CardTitle>
        <CardDescription className="text-center">
          {showSubscriptionText 
            ? "Complete your registration to access your purchased plan" 
            : "Enter your information to create an account"}
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input 
              id="name" 
              placeholder="Your name" 
              value={name}
              onChange={(e) => setName(e.target.value)}
              required 
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input 
              id="email" 
              type="email" 
              placeholder="you@example.com" 
              value={emailValue}
              onChange={(e) => setEmailValue(e.target.value)}
              required 
              readOnly={isEmailReadOnly}
              className={isEmailReadOnly ? "bg-gray-100" : ""}
            />
            {isEmailReadOnly && (
              <p className="text-xs text-muted-foreground">
                Email is pre-filled from your purchase
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input 
              id="password" 
              type="password" 
              placeholder="Create a password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required 
              minLength={8}
            />
            <p className="text-xs text-muted-foreground">
              Password must be at least 8 characters long
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input 
              id="confirmPassword" 
              type="password" 
              placeholder="Confirm your password" 
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required 
              minLength={8}
            />
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="terms" 
              checked={agreed}
              onCheckedChange={(checked) => setAgreed(checked as boolean)}
            />
            <label
              htmlFor="terms"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              I agree to the{" "}
              <Link to="/terms" className="text-krix-purple hover:underline">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="text-krix-purple hover:underline">
                Privacy Policy
              </Link>
            </label>
          </div>
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          <LaunchButton 
            type="submit" 
            variant="cta" 
            className="w-full" 
            disabled={isLoading || isRedirecting}
          >
            {isLoading ? "Creating account..." : 
             isRedirecting ? "Redirecting to Krix Chat..." :
             (showSubscriptionText ? "Complete Registration & Go to Krix Chat" : "Create account")}
          </LaunchButton>
          <div className="text-center text-sm">
            Already have an account?{" "}
            <Link to="/login" className="text-krix-purple hover:underline">
              Log in
            </Link>
          </div>
        </CardFooter>
      </form>
    </Card>
  );
};

export default SignupForm;
